import React, { useRef, useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    TextField,
    useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useLazyGetDriverStatisticsQuery } from "store/services/driverApi";

const DriverActivityChart = ({ driverId }) => {
    const chartRef = useRef(null);
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const [timeRange, setTimeRange] = useState("last7days");
    const [customFromDate, setCustomFromDate] = useState(moment().subtract(6, "days").toDate());
    const [customToDate, setCustomToDate] = useState(moment().toDate());

    const [fetchDriverActivity, { data: driverActivityRs, isLoading }] = useLazyGetDriverStatisticsQuery();

    // Get date range
    const getDateRange = useCallback((range) => {
        switch (range) {
            case "last7days":
                return {
                    startDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
                    endDate: moment().format("YYYY-MM-DD"),
                };
            case "onemonth":
                return {
                    startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
                    endDate: moment().format("YYYY-MM-DD"),
                };
            case "custom":
                return {
                    startDate: moment(customFromDate).format("YYYY-MM-DD"),
                    endDate: moment(customToDate).format("YYYY-MM-DD"),
                };
            default:
                return {
                    startDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
                    endDate: moment().format("YYYY-MM-DD"),
                };
        }
    }, [customFromDate, customToDate]);

    // Fetch data
    const fetchData = useCallback(() => {
        if (!driverId) return;
        const { startDate, endDate } = getDateRange(timeRange);
        fetchDriverActivity({ driverId, params: { startDate, endDate } });
    }, [driverId, timeRange, getDateRange, fetchDriverActivity]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Chart data
    const chartData = React.useMemo(() => {
        if (driverActivityRs?.activityCounts) {
            const { collectionCount, loadingCount, deliveryCount } = driverActivityRs.activityCounts;
            return [
                { activity: "Collections", count: collectionCount || 0 },
                { activity: "Loading", count: loadingCount || 0 },
                { activity: "Deliveries", count: deliveryCount || 0 },
            ];
        }
        return [];
    }, [driverActivityRs]);

    const activities = chartData.map(item => item.activity);
    const counts = chartData.map(item => item.count);
    const maxValue = Math.max(Math.max(...counts) + 2, 5);

    // Menu handlers



    if (!driverId) return <Typography>No driver selected</Typography>;
    if (isLoading) return <Typography>Loading...</Typography>;

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box ref={chartRef} sx={{ p: 2, boxShadow: 2, borderRadius: 2 }}>
                {/* Filters */}
                <Box display="flex" gap={2} mb={2} alignItems="center">
                    <BaseSelect
                        label="Range"
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        options={[
                            { label: "Last 7 Days", value: "last7days" },
                            { label: "One Month", value: "onemonth" },
                            { label: "Custom", value: "custom" },
                        ]}
                        getOptionLabel={(opt) => opt.label}
                        getOptionValue={(opt) => opt.value}
                        size="small"
                        sx={{ minWidth: 140 }}
                    />

                    {timeRange === "custom" && (
                        <>
                            <DatePicker
                                label="From"
                                value={customFromDate}
                                onChange={setCustomFromDate}
                                renderInput={(params) => <TextField {...params} size="small" />}
                            />
                            <DatePicker
                                label="To"
                                value={customToDate}
                                onChange={setCustomToDate}
                                renderInput={(params) => <TextField {...params} size="small" />}
                            />
                        </>
                    )}
                </Box>

                {/* Chart */}
                {chartData.length > 0 ? (
                    <BarChart
                        xAxis={[{ scaleType: "band", data: activities }]}
                        yAxis={[{ min: 0, max: maxValue }]}
                        series={[{ data: counts }]}
                        width={800}
                        height={400}
                        colors={[theme.palette.primary.main]}
                    />
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <Typography color="text.secondary">No data available</Typography>
                    </Box>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default DriverActivityChart;